/**
 * stl-card.js - behaviour for snippets/stl-card.liquid (slice 3).
 *
 * The card is server-rendered whole: photo, name, price, the daisy, the colour circles, the
 * pooled stars and the size row are all real markup, and every size button is a real quick add
 * through @stl/core's data-stl-add. With no JavaScript the card still works: the circles are
 * links to the other colourways and the sizes are plain buttons.
 *
 * This module adds the three things markup cannot do:
 *   1. the phone's + opens the bar (one card at a time inside a grid)
 *   2. in mode 'style' ONLY, a colour circle swaps the photo, name, price, sizes and badge IN
 *      PLACE instead of navigating, because there one card stands for five colours. In mode
 *      'colour' one tile is one colourway and the circles are left alone: they are real links to
 *      the other colourways' product pages and the browser follows them (23 Sep 2026)
 *   3. a size filter re-badges a card "Sold out in S" and dims it
 *
 * Every card prints its style data as <script type="application/json" data-stl-style>. On the
 * collection grid each card prints only its own colourway (scope 'self') because every colourway
 * of the style is on the page; anywhere else it prints the whole style. Either way the index
 * below ends up with the same shape, so nothing downstream has to care which it was.
 *
 * It is loaded once per page by sections/stl-collection.liquid and
 * sections/stl-wishlist-drawer.liquid (which is in the footer group, so on every page). A module
 * URL is only evaluated once per document, so loading it twice is free.
 */

import { onReady, delegate, money, wishlist } from '@stl/core';

const ON = 'stl-is-on';
const OPEN = 'stl-qa-open';
const DIM = 'stl-dim';

/* ------------------------------------------------------------------ the style index */

/** key -> { key, name, family, category, fabric, rating, rating_count, handles[], colours{} } */
const styles = new Map();
/** handle -> colourway object */
const colours = new Map();
const seen = new WeakSet();

function readCardData(el) {
  try {
    return JSON.parse(el.textContent || 'null');
  } catch (e) {
    return null;
  }
}

/**
 * Reads every style-data block inside root into the index. Safe to call again: a block already
 * read is skipped, and a colourway already known is not overwritten by a thinner copy.
 */
export function indexStyles(root) {
  const scope = root || document;
  const blocks = scope.querySelectorAll('script[data-stl-style]');
  blocks.forEach((el) => {
    if (seen.has(el)) return;
    seen.add(el);
    const data = readCardData(el);
    if (!data || !data.key) return;
    let style = styles.get(data.key);
    if (!style) {
      style = {
        key: data.key,
        name: data.name || '',
        family: data.family || '',
        category: data.category || '',
        fabric: data.fabric || '',
        rating: data.rating === null || data.rating === undefined ? null : Number(data.rating),
        rating_count: Number(data.rating_count) || 0,
        handles: Array.isArray(data.handles) ? data.handles.slice() : [],
        colours: new Map(),
      };
      styles.set(data.key, style);
    }
    if (!style.handles.length && Array.isArray(data.handles)) style.handles = data.handles.slice();
    (data.colourways || []).forEach((colour) => {
      if (!colour || !colour.handle) return;
      colour.styleKey = data.key;
      if (!style.colours.has(colour.handle) || (colour.sizes || []).length) {
        style.colours.set(colour.handle, colour);
        colours.set(colour.handle, colour);
      }
    });
  });
  return styles;
}

/** The style record for a key, or null. */
export function styleOf(key) {
  return styles.get(key) || null;
}

/** The colourway record for a product handle, or null. */
export function colourOf(handle) {
  return colours.get(handle) || null;
}

/** The style's colourways in the style's own order (Sophie's order), skipping any not on the page. */
export function colourwaysOf(style) {
  if (!style) return [];
  const out = [];
  style.handles.forEach((handle) => {
    const colour = style.colours.get(handle);
    if (colour) out.push(colour);
  });
  style.colours.forEach((colour) => {
    if (!out.includes(colour)) out.push(colour);
  });
  return out;
}

/** Every style the index knows, in insertion order. */
export function allStyles() {
  return [...styles.values()];
}

/* ------------------------------------------------------------------ sizes */

/**
 * Does a chosen size match a variant's option value? Shopify's values are plain letters on 40
 * garments, and the Flora Wrap Skirt is sold in bands ("XS - S", "M - L", "XL - 2XL", spaces and
 * all). A band matches any of the letters in it, so choosing S finds the Flora band that holds
 * an S. The mock could never match Flora at all; this is a deliberate fix, logged in the notes.
 */
export function sizeMatches(chosen, value) {
  if (!chosen || !chosen.length) return true;
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return false;
  const parts = raw.split('-').map((part) => part.trim()).filter(Boolean);
  const tokens = new Set(parts);
  tokens.add(raw);
  return chosen.some((size) => tokens.has(String(size).trim().toUpperCase()));
}

/** Units of a colourway in the chosen sizes (0 when nothing is chosen), for picking a colour. */
export function unitsInSizes(colour, chosen) {
  if (!colour || !chosen || !chosen.length) return 0;
  return (colour.sizes || []).reduce((sum, size) => (sizeMatches(chosen, size.value) ? sum + (Number(size.q) || 0) : sum), 0);
}

/** Total units of a colourway, capped per size by the style snippet. */
export function unitsOf(colour) {
  if (!colour) return 0;
  return (colour.sizes || []).reduce((sum, size) => sum + (Number(size.q) || 0), 0);
}

/** Is this colourway buyable in at least one of the chosen sizes? */
export function inChosenSizes(colour, chosen) {
  if (!colour) return false;
  if (!chosen || !chosen.length) return !!colour.available;
  return (colour.sizes || []).some((size) => sizeMatches(chosen, size.value) && Number(size.q) > 0);
}

/* ------------------------------------------------------------------ painting a card */

/* The widths snippets/stl-card.liquid asks for, minus 1400: snippets/stl-style.liquid publishes
   each colourway's image at width 1200, so 1200 is the largest file a repaint can honestly name.
   Keep this list and the snippet's in step. */
const CARD_WIDTHS = [360, 540, 720, 900, 1200];
const WIDTH_PARAM = /([?&]width=)\d+/;

/** One candidate of a Shopify image URL that already carries a width parameter. */
export function imageAtWidth(url, width) {
  if (!url || !WIDTH_PARAM.test(url)) return url || '';
  return url.replace(WIDTH_PARAM, '$1' + width);
}

/**
 * A real srcset for a Shopify image URL, so a repainted card keeps the responsive behaviour the
 * server rendered. Stripping srcset and pinning src to the 1200px file made every card on a
 * phone download a 1200px photo for a 193px slot.
 */
export function srcsetFor(url, widths) {
  if (!url || !WIDTH_PARAM.test(url)) return '';
  return (widths || CARD_WIDTHS).map((w) => imageAtWidth(url, w) + ' ' + w + 'w').join(', ');
}

function escapeAttr(value) {
  return String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sizesHTML(colour) {
  return (colour.sizes || [])
    .map((size) => {
      const value = escapeAttr(size.value);
      if (size.available) {
        return (
          '<button type="button" data-stl-add="' + escapeAttr(size.id) + '" data-stl-size="' + value +
          '" aria-label="Add size ' + value + '">' + value + '</button>'
        );
      }
      return (
        '<button type="button" class="stl-off" disabled data-stl-size="' + value +
        '" aria-label="' + value + ' sold out">' + value + '</button>'
      );
    })
    .join('');
}

/**
 * Paints a card as one colourway, under an optional size filter. Everything the mock's cardHTML
 * decided is decided here, on the same rules:
 *   - the badge judges the colourway SHOWN, not the style
 *   - "Sold out" when the colourway has nothing left, "Sold out in S" when it has nothing in the
 *     chosen size, and the photo fades either way
 *   - the + is gone when the colourway is fully sold out
 *   - a strikethrough only when compare-at is higher than the price
 */
export function paintCard(card, colour, options) {
  if (!card || !colour) return;
  const opts = options || {};
  const chosen = opts.sizes || [];
  const style = styleOf(colour.styleKey) || styleOf(card.getAttribute('data-stl-style'));
  /* read BEFORE the attribute is rewritten: it says which colourway is on screen right now, and
     therefore whether the photo has to change at all */
  const shown = card.getAttribute('data-stl-card');
  const soldOut = !colour.available;
  const okSize = !chosen.length || inChosenSizes(colour, chosen);
  const badge = soldOut ? 'Sold out' : okSize ? '' : 'Sold out in ' + chosen.join(', ');
  const name = (style && style.name ? style.name : colour.title || '') + (colour.label ? ' - ' + colour.label : '');

  card.setAttribute('data-stl-card', colour.handle);
  card.classList.toggle(DIM, soldOut || !okSize);

  const photoLink = card.querySelector('.stl-pcard-img');
  if (photoLink) photoLink.setAttribute('href', colour.url);
  const nameLink = card.querySelector('.stl-pcard-name');
  if (nameLink) {
    nameLink.setAttribute('href', colour.url);
    nameLink.textContent = name;
  }

  /* The photo is touched ONLY when the card is changing colourway. On a first paint the server
     already rendered this colourway's photo, with its srcset, its sizes and its loading and
     fetchpriority hints, and rewriting it would throw all four away: every card on the page then
     downloaded the 1200px file for a 193px slot. When the colourway really does change, the
     srcset is rebuilt rather than stripped and `sizes` is left exactly as the snippet set it. */
  const img = card.querySelector('.stl-pcard-img img');
  if (img && colour.image && shown !== colour.handle) {
    const srcset = srcsetFor(colour.image);
    if (srcset) img.setAttribute('srcset', srcset);
    else img.removeAttribute('srcset');
    img.setAttribute('src', colour.image);
    img.setAttribute('alt', name);
    if (colour.image_width) img.setAttribute('width', colour.image_width);
    if (colour.image_height) img.setAttribute('height', colour.image_height);
  }

  const badgeEl = card.querySelector('[data-stl-badge]');
  if (badgeEl) {
    badgeEl.textContent = badge || 'Sold out';
    badgeEl.hidden = !badge;
  }

  const heart = card.querySelector('[data-stl-wish]');
  if (heart) {
    heart.setAttribute('data-stl-wish', colour.handle);
    /* The label names the piece (snippets/stl-card.liquid renders it that way), so it has to
       follow the colourway the card is now showing, exactly as the name link and the photo's alt
       do above. Left alone it would say "Save Wrap Top - Ivory to wishlist" over a card showing
       Navy. paintWish() in @stl/core only writes aria-pressed, so nothing else touches it. */
    if (name) heart.setAttribute('aria-label', 'Save ' + name + ' to wishlist');
  }

  const price = card.querySelector('[data-stl-price]');
  if (price) {
    const was = colour.compare_at && colour.compare_at > colour.price
      ? '<s class="stl-pcard-was"><span class="stl-vh">Was </span>' + money(colour.compare_at) + '</s>'
      : '';
    price.innerHTML = was + '<span data-stl-now>' + money(colour.price) + '</span>';
  }

  const rating = card.querySelector('.stl-rating');
  if (rating) rating.setAttribute('href', colour.url + '#reviews');

  const row = card.querySelector('[data-stl-qa-sizes]');
  if (row) row.innerHTML = sizesHTML(colour);

  card.querySelectorAll('[data-stl-sw]').forEach((swatch) => {
    const isCurrent = swatch.getAttribute('data-stl-sw') === colour.handle;
    swatch.classList.toggle(ON, isCurrent);
    if (isCurrent) swatch.setAttribute('aria-current', 'page');
    else swatch.removeAttribute('aria-current');
  });

  /* the + exists only while there is something to add, exactly as the mock has it */
  const qa = card.querySelector('[data-stl-qa]');
  const top = card.querySelector('.stl-pcard-top');
  if (soldOut) {
    if (qa) qa.remove();
    /* The colour circles live inside the bar, and the + is what opens it on a phone. Closing the
       bar here as well as removing the + left the shopper stranded on the colourway they had just
       tapped, with no way back to the others. So when the style has other colourways to go to,
       an open bar stays open. A single-colourway style has nothing to return to, and closes. */
    const siblings = colourwaysOf(style).length;
    if (siblings < 2) card.classList.remove(OPEN);
  } else if (!qa && top && (colour.sizes || []).length) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'stl-qa';
    button.setAttribute('data-stl-qa', '');
    /* Named for the same reason as the heart: a grid renders one + per card and "Choose a size"
       on its own does not say which piece the sizes belong to. */
    button.setAttribute('aria-label', name ? 'Choose a size for ' + name : 'Choose a size');
    button.setAttribute('aria-expanded', 'false');
    button.textContent = '+';
    const heartEl = top.querySelector('.stl-heart');
    top.insertBefore(button, heartEl || null);
  }

  card.__stlColour = colour;
  card.__stlSizes = chosen;
  wishlist.sync(card);
}

/** Re-badges a card for a new size filter without changing the colourway it shows. */
export function setCardSizes(card, chosen) {
  const colour = card.__stlColour || colourOf(card.getAttribute('data-stl-card'));
  if (colour) paintCard(card, colour, { sizes: chosen || [] });
}

/* ------------------------------------------------------------------ wiring, once */

let booted = false;

function boot() {
  if (booted) return;
  booted = true;

  indexStyles(document);

  /* the phone's +: one open bar at a time inside a grid */
  delegate(document, 'click', '[data-stl-qa]', (event, el) => {
    event.preventDefault();
    event.stopPropagation();
    const card = el.closest('.stl-pcard');
    if (!card) return;
    const root = card.parentElement || document;
    root.querySelectorAll('.stl-pcard.' + OPEN).forEach((other) => {
      if (other !== card) {
        other.classList.remove(OPEN);
        const button = other.querySelector('[data-stl-qa]');
        if (button) button.setAttribute('aria-expanded', 'false');
      }
    });
    const open = card.classList.toggle(OPEN);
    el.setAttribute('aria-expanded', open ? 'true' : 'false');
  });

  /* A colour circle is a real link to that colourway's product page, rendered by
     snippets/stl-style.liquid with the market prefix already on it.

     ON A ONE-TILE-PER-COLOURWAY GRID THE LINK IS THE WHOLE BEHAVIOUR (23 Sep 2026). A tile
     stands for one colourway and nothing on it swaps: the photo, the name, the price and the size
     row all stay the tile's own, and tapping another colour takes the shopper to that colour's own
     product page. So this handler stands down for a card in mode 'colour', which lets the browser
     follow the href: it works with no JavaScript, it right-clicks, and a crawler sees every
     colourway linked from the grid.

     A card in mode 'style' stands for the whole style and still swaps in place, as it always has,
     because there the circles are how one card shows five colours. */
  delegate(document, 'click', '.stl-pcard [data-stl-sw]', (event, el) => {
    const card = el.closest('.stl-pcard');
    if (!card) return;
    if (card.getAttribute('data-stl-mode') === 'colour') return;
    const handle = el.getAttribute('data-stl-sw');
    const colour = colourOf(handle);
    if (!colour) return; /* not on this page: let the link do its job */
    event.preventDefault();
    paintCard(card, colour, { sizes: card.__stlSizes || [] });
    card.dispatchEvent(new CustomEvent('stl:card:colour', { bubbles: true, detail: { handle } }));
  });

  /* adding from a card closes the bar, as the mock does */
  delegate(document, 'click', '.stl-pcard [data-stl-add]', (event, el) => {
    const card = el.closest('.stl-pcard');
    if (card) card.classList.remove(OPEN);
  });

  /* remember which colourway each card opened on */
  document.querySelectorAll('.stl-pcard').forEach((card) => {
    const colour = colourOf(card.getAttribute('data-stl-card'));
    if (colour) card.__stlColour = colour;
  });
}

onReady(boot);

export default { indexStyles, styleOf, colourOf, colourwaysOf, allStyles, paintCard, setCardSizes, sizeMatches, inChosenSizes, unitsInSizes, unitsOf, imageAtWidth, srcsetFor };
